/**
 * Zod schemas for Meraki API responses.
 *
 * Mirrors the legacy `app_meraki/static/app_meraki/ts/schemas/provisioning.ts`
 * but freed from the Django-relative import path. We use these schemas for
 * runtime validation of API responses so a backend-shape regression fails
 * fast and visibly instead of corrupting UI state.
 */
import { z } from "zod";

/**
 * A single workflow the user can run against a Meraki network.
 *
 * `recommendedScore` is 0–100; the API computes it from site posture +
 * recent activity. `scoreReasons` are short human-readable bullets that
 * back the score (shown as a popover/tooltip in the UI).
 */
const WorkflowSchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  path: z.string(),
  recommendedScore: z.number().min(0).max(100),
  scoreReasons: z.array(z.string()),
});

export const WorkflowListSchema = z.array(WorkflowSchema);

export type Workflow = z.infer<typeof WorkflowSchema>;

/* ============================================ */
/* MERAKI KEYS                                  */
/* ============================================ */

/** Meraki dashboard regions Opus can talk to. The legacy app supports three. */
const MerakiRegionSchema = z.enum(["global", "canada", "china"]);
export type MerakiRegion = z.infer<typeof MerakiRegionSchema>;

/**
 * A registered Meraki API key. The actual key string is masked from the
 * server (only the last 4 chars are returned) — never assume `key` is the
 * full secret.
 */
const MerakiKeySchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Server returns a masked value like "•••• ABCD" — display-only. */
  key: z.string(),
  region: MerakiRegionSchema,
  active: z.boolean(),
  createdAt: z.string(),
  /** Number of networks this key is currently associated with. The
   *  server returns null when the inventory probe was skipped (inactive
   *  key) or 404'd mid-fan-out. Treat null as "unknown" → undefined so
   *  the UI's "{n} networks" line just disappears instead of crashing
   *  on a fixture-data fallback. */
  networkCount: z.number().nullish().transform((v) => v ?? undefined),
});
export type MerakiKey = z.infer<typeof MerakiKeySchema>;
export const MerakiKeyListSchema = z.array(MerakiKeySchema);

/**
 * Payload shape for creating or activating a key.
 * `id` is present on update/activate, absent on create.
 */
export const MerakiKeyMutationSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, "Name is required"),
  key: z.string().min(20, "Meraki keys are 40 hex characters"),
  region: MerakiRegionSchema,
  active: z.boolean(),
});
export type MerakiKeyMutation = z.infer<typeof MerakiKeyMutationSchema>;

/**
 * Celery task envelope returned when creating a key (the network-association
 * sync runs in the background). The progress endpoint is polled to drive the
 * progress bar.
 */
export const CeleryTaskSchema = z.object({
  taskId: z.string(),
});
export type CeleryTask = z.infer<typeof CeleryTaskSchema>;

/**
 * Status snapshot returned by the Celery progress endpoint.
 */
export const CeleryProgressSchema = z.object({
  state: z.enum(["PENDING", "PROGRESS", "SUCCESS", "FAILURE"]),
  current: z.number().optional(),
  total: z.number().optional(),
  message: z.string().optional(),
});
export type CeleryProgress = z.infer<typeof CeleryProgressSchema>;

/* ============================================ */
/* MERAKI NETWORKS                              */
/* ============================================ */

/** Lightweight network shape — matches the legacy MerakiNetworkSelector. */
const MerakiNetworkSchema = z.object({
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  // Some tenants/orgs return null for organizationName — accept it.
  organizationName: z.string().nullish().transform((v) => v ?? ""),
  /** Comma-joined product types (e.g. "appliance, switch, wireless"). */
  productTypes: z.array(z.string()),
  /** Total devices currently associated with this network. Some orgs
   *  return null when the inventory call 404s server-side; treat as 0. */
  deviceCount: z.number().nullish().transform((v) => v ?? 0),
});
export type MerakiNetwork = z.infer<typeof MerakiNetworkSchema>;
export const MerakiNetworkListSchema = z.array(MerakiNetworkSchema);

/* ============================================ */
/* DEBRANDING — NETWORK BACKUPS                 */
/* ============================================ */

/**
 * One stored backup of a network's configuration. Mirrors the Django
 * `BackupMetadata` dataclass: a backup is "consumed" when it has been
 * restored into a target network, at which point the restore_* fields
 * are populated.
 */
const NetworkBackupSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  networkId: z.string(),
  networkName: z.string(),
  restoredAt: z.string().nullable().optional(),
  restoredBy: z.string().nullable().optional(),
  restoredIntoNetworkId: z.string().nullable().optional(),
  /** Snapshot stats so the row can show "8 SSIDs · 24 ports" without a roundtrip. */
  summary: z
    .object({
      ssidCount: z.number().optional(),
      portCount: z.number().optional(),
      vlanCount: z.number().optional(),
      deviceCount: z.number().optional(),
    })
    .optional(),
});
export type NetworkBackup = z.infer<typeof NetworkBackupSchema>;
export const NetworkBackupListSchema = z.array(NetworkBackupSchema);

/* ============================================ */
/* API SUITE — RUNNABLE OPERATIONS              */
/* ============================================ */

/**
 * One Meraki API operation the user can run from the API Suite page.
 * Categories let us group related operations in the UI (e.g. "Device admin",
 * "Reporting", "VLAN cleanup").
 */
const ApiOperationSchema = z.object({
  slug: z.string(),
  name: z.string(),
  category: z.string(),
  description: z.string(),
  /** Higher = more impactful; 1=read-only safe, 5=mutates live config. */
  destructiveness: z.number().min(1).max(5),
  /** Last time anyone ran this operation in this org. ISO string. */
  lastRunAt: z.string().nullable().optional(),
});
export type ApiOperation = z.infer<typeof ApiOperationSchema>;
export const ApiOperationListSchema = z.array(ApiOperationSchema);

/* ============================================ */
/* UPDATE DEVICES — TABLE ROWS                  */
/* ============================================ */

/**
 * A device row as shown in the Update Devices spreadsheet-style editor.
 * Keep loose where the legacy app accepts mixed types (notes, tags).
 */
const DeviceRowSchema = z.object({
  serial: z.string(),
  name: z.string().nullable(),
  model: z.string().optional(),
  mac: z.string().optional(),
  lanIp: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  firmware: z.string().optional(),
  /** Comma-separated string in the API; we normalize to array client-side. */
  tags: z.array(z.string()),
  notes: z.string().nullable().optional(),
});
export type DeviceRow = z.infer<typeof DeviceRowSchema>;
export const DeviceRowListSchema = z.array(DeviceRowSchema);

/* ============================================ */
/* SWITCH PORTS                                 */
/* ============================================ */

const SwitchPortSchema = z.object({
  portId: z.string(),
  deviceSerial: z.string(),
  deviceName: z.string().nullable(),
  /** "trunk" | "access" — preserve as string for forward compatibility. */
  type: z.string(),
  vlan: z.string().nullable(),
  allowedVlans: z.string().nullable(),
  enabled: z.boolean().nullable(),
  name: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  /** Optional CDP/LLDP neighbor — when present, show in tooltip/expander. */
  neighbor: z
    .object({
      systemName: z.string().nullable().optional(),
      portId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type SwitchPort = z.infer<typeof SwitchPortSchema>;
export const SwitchPortListSchema = z.array(SwitchPortSchema);

/* ============================================ */
/* ONBOARDING — STATUS PER STEP                 */
/* ============================================ */

/** A single onboarding step — the legacy template lays out ~5 of them. */
export const OnboardingStepStatusSchema = z.object({
  slug: z.string(),
  title: z.string(),
  /** "pending" | "running" | "ok" | "error" — drives the Stepper styling. */
  state: z.enum(["pending", "running", "ok", "error"]),
  /** Optional one-line summary surfaced under the title. */
  detail: z.string().optional(),
});
export type OnboardingStepStatus = z.infer<typeof OnboardingStepStatusSchema>;
export const OnboardingStatusSchema = z.array(OnboardingStepStatusSchema);
